/**
 * AuthContext — dual-auth provider supporting both legacy SourceLink tokens
 * and Entra ID SSO (MSAL) sessions.
 *
 * Mirrors Portal-Ui's AuthContext pattern:
 *   - `auth`       — legacy login (bearerToken, refreshTokenId, expiresAtUtc, identity)
 *   - `ssoSession` — Entra ID SSO (claims from whoAmI endpoint)
 *
 * Maintains backward compatibility with the existing `user`, `isAuthenticated`,
 * `login()`, `logout()`, and `setToken()` interface consumed by Layout, SecurityTab,
 * TaskFilters, ProtectedRoute, etc.
 */
import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  type TokenMeta,
  type DevLoginRequest,
  devLogin,
  logout as legacyLogout,
  getTokenMeta,
  isAuthenticated as checkLegacyAuth,
  storeRawToken,
} from '../services/authService';
import { normalizeRoles } from '../utils/authUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Legacy auth identity returned by loginUser / decodeToken. */
export interface LegacyIdentity {
  userId?: number;
  oid?: string;
  email?: string;
  displayName?: string;
  roles?: string[];
  [key: string]: unknown;
}

/** Data stored in localStorage under 'auth' for legacy login. */
export interface LegacyAuth {
  bearerToken: string;
  refreshTokenId: string;
  expiresAtUtc: string | null;
  identity: LegacyIdentity;
}

/** whoAmI claims from Entra ID SSO. */
export interface SsoClaims {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  oid?: string;
  cus_roles?: string[];
  is_reg?: string | boolean;
  usr_status?: string;
  usr_auto_reg?: string | boolean;
  schwab_ofc_id?: string;
  [key: string]: unknown;
}

/** Data stored in localStorage under 'ssoSession'. */
export interface SsoSession {
  claims: SsoClaims;
}

/**
 * Backward-compatible user object derived from either auth source.
 * Consumed by Layout, SecurityTab, TaskFilters, PagePermissionContext, etc.
 */
export interface AuthUser {
  userId: number;
  email: string;
  displayName: string;
  roles: string[];
  issuedAtUtc: string;
  expiresAtUtc: string | null;
}

export interface AuthContextValue {
  // ── Backward-compatible surface ──
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Dev-token login (existing callers). */
  login: (request?: DevLoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  /** Store a raw token + metadata (used by SecurityTab dev token generator). */
  setToken: (token: string, meta: TokenMeta) => void;

  // ── Dual-auth surface (new) ──
  /** Legacy SourceLink auth state (bearerToken + identity). */
  auth: LegacyAuth | null;
  /** Entra ID SSO session state (whoAmI claims). */
  ssoSession: SsoSession | null;
  /** True until initial session restoration from localStorage completes. */
  isAuthLoading: boolean;
  /** Store a legacy auth result (from loginUser). Clears ssoSession. */
  loginLegacy: (authResult: LegacyAuth) => void;
  /** Store SSO claims (from whoAmI). Clears legacy auth. */
  loginSso: (claims: SsoClaims) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function metaToUser(meta: TokenMeta): AuthUser {
  return {
    userId: meta.userId,
    email: meta.email,
    displayName: meta.displayName,
    roles: meta.roles,
    issuedAtUtc: meta.issuedAtUtc,
    expiresAtUtc: meta.expiresAtUtc,
  };
}

function legacyAuthToUser(auth: LegacyAuth): AuthUser {
  const identity = auth.identity ?? {};
  return {
    userId: identity.userId ?? 0,
    email: identity.email ?? '',
    displayName: identity.displayName ?? '',
    roles: normalizeRoles(identity.roles),
    issuedAtUtc: new Date().toISOString(),
    expiresAtUtc: auth.expiresAtUtc,
  };
}

function ssoToUser(session: SsoSession): AuthUser {
  const c = session.claims;
  return {
    userId: 0, // SSO users don't have a legacy userId
    email: (c.email as string) ?? '',
    displayName: (c.name as string) ?? `${c.given_name ?? ''} ${c.family_name ?? ''}`.trim(),
    roles: normalizeRoles(c.cus_roles),
    issuedAtUtc: new Date().toISOString(),
    expiresAtUtc: null,
  };
}

function readLegacyAuthFromStorage(): LegacyAuth | null {
  try {
    const stored = localStorage.getItem('auth');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<LegacyAuth>;
    if (!parsed?.bearerToken || !parsed.identity) {
      localStorage.removeItem('auth');
      return null;
    }
    return parsed as LegacyAuth;
  } catch {
    return null;
  }
}

function readSsoSessionFromStorage(): SsoSession | null {
  try {
    const stored = localStorage.getItem('ssoSession');
    return stored ? (JSON.parse(stored) as SsoSession) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<LegacyAuth | null>(null);
  const [ssoSession, setSsoSession] = useState<SsoSession | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(() => {
    // Signal to API interceptors that auth is still loading so they
    // don't clear tokens and hard-redirect on transient 401s during refresh.
    // Guarded: sessionStorage can throw in privacy modes / disabled-storage
    // environments, and this initializer runs during provider render.
    try {
      sessionStorage.setItem('__auth_loading', '1');
    } catch {
      // ignore — the flag is advisory
    }
    return true;
  });

  // Derive backward-compatible `user` from whichever auth source is active.
  // Also consider in-memory token store (for dev login which bypasses localStorage).
  const [devUser, setDevUser] = useState<AuthUser | null>(() => {
    if (checkLegacyAuth()) {
      const meta = getTokenMeta();
      return meta ? metaToUser(meta) : null;
    }
    return null;
  });

  // ── Session restoration on mount ──
  useEffect(() => {
    const storedAuth = readLegacyAuthFromStorage();
    const storedSso = readSsoSessionFromStorage();

    // Validate token freshness before restoring legacy auth (Fix 5/6)
    if (storedAuth) {
      if (storedAuth.expiresAtUtc && new Date(storedAuth.expiresAtUtc) <= new Date()) {
        // Token is expired — clear it instead of restoring
        localStorage.removeItem('auth');
      } else {
        setAuth(storedAuth);
        // Repopulate the in-memory token store so API interceptors can
        // attach the Bearer token immediately (prevents 401 → redirect loop on refresh)
        const user = legacyAuthToUser(storedAuth);
        storeRawToken(storedAuth.bearerToken, {
          userId: user.userId,
          email: user.email,
          displayName: user.displayName,
          roles: user.roles,
          issuedAtUtc: user.issuedAtUtc,
          expiresAtUtc: storedAuth.expiresAtUtc,
        });
        setDevUser(user);
      }
    }

    if (storedSso) setSsoSession(storedSso);
    setIsAuthLoading(false);
    // Clear the flag so API interceptors know session restoration is done
    try {
      sessionStorage.removeItem('__auth_loading');
    } catch {
      // ignore — storage may be disabled (e.g. private browsing)
    }
  }, []);

  // Check for token expiry periodically (every 30 seconds).
  // Skips SSO sessions — those don't populate the in-memory tokenStore,
  // so checkLegacyAuth() would always return false and clear devUser.
  useEffect(() => {
    if (!devUser && !auth) return;
    if (ssoSession) return;

    const interval = setInterval(() => {
      if (devUser && !checkLegacyAuth()) {
        setDevUser(null);
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [devUser, auth, ssoSession]);

  // ── Legacy login (from loginUser endpoint) ──
  const loginLegacy = useCallback((authResult: LegacyAuth) => {
    setAuth(authResult);
    localStorage.setItem('auth', JSON.stringify(authResult));
    setSsoSession(null);
    localStorage.removeItem('ssoSession');
    // Also set the backward-compat user
    setDevUser(legacyAuthToUser(authResult));
    // Populate the in-memory tokenStore so apiClient.ts can read it (Fix 9)
    const user = legacyAuthToUser(authResult);
    storeRawToken(authResult.bearerToken, {
      userId: user.userId,
      email: user.email,
      displayName: user.displayName,
      roles: user.roles,
      issuedAtUtc: user.issuedAtUtc,
      expiresAtUtc: authResult.expiresAtUtc,
    });
  }, []);

  // ── SSO login (from whoAmI claims) ──
  const loginSso = useCallback((claims: SsoClaims) => {
    const normalizedClaims: SsoClaims = { ...claims };
    normalizedClaims.cus_roles = normalizeRoles(claims.cus_roles);

    const ssoData: SsoSession = { claims: normalizedClaims };
    setSsoSession(ssoData);
    localStorage.setItem('ssoSession', JSON.stringify(ssoData));
    setAuth(null);
    localStorage.removeItem('auth');
    // Also set the backward-compat user. SSO tokens are managed by MSAL —
    // we deliberately don't populate the in-memory tokenStore here. The
    // apiClient request interceptor calls getAccessTokenAsync() which
    // pulls a fresh access token from MSAL on every request.
    setDevUser(ssoToUser(ssoData));
  }, []);

  // ── Dev login (backward compat — calls POST /api/dev/token) ──
  const login = useCallback(async (request?: DevLoginRequest) => {
    const meta = await devLogin(request);
    setDevUser(metaToUser(meta));
    // Dev login doesn't use localStorage dual-auth — it uses in-memory tokenStore
  }, []);

  // ── Logout ──
  const logout = useCallback(async () => {
    const wasSsoUser = !!ssoSession;

    // Try to revoke legacy token if present
    try {
      const { revokeToken } = await import('../services/authentication');
      await revokeToken('User logout');
    } catch {
      // Continue with logout even if revoke fails or module not available
    }

    // Clear local state
    setAuth(null);
    setSsoSession(null);
    setDevUser(null);
    localStorage.removeItem('auth');
    localStorage.removeItem('ssoSession');

    // Clear in-memory token store (backward compat)
    legacyLogout();

    // Clear MSAL cache for SSO users
    if (wasSsoUser) {
      try {
        const { getMsalInstance } = await import('../services/msalInstance');
        const msalInstance = getMsalInstance();
        await msalInstance.clearCache();
      } catch {
        // Continue even if MSAL clear fails
      }
    }
  }, [ssoSession]);

  // ── Store raw token (backward compat for SecurityTab) ──
  const setToken = useCallback((token: string, meta: TokenMeta) => {
    storeRawToken(token, meta);
    setDevUser(metaToUser(meta));
  }, []);

  // ── Derived state ──
  const user = devUser
    ?? (auth ? legacyAuthToUser(auth) : null)
    ?? (ssoSession ? ssoToUser(ssoSession) : null);

  const isAuthenticated =
    (devUser !== null && checkLegacyAuth())
    || auth !== null
    || ssoSession !== null;

  return (
    <AuthContext.Provider
      value={{
        // Backward-compatible surface
        user,
        isAuthenticated,
        isLoading: isAuthLoading,
        login,
        logout,
        setToken,
        // Dual-auth surface
        auth,
        ssoSession,
        isAuthLoading,
        loginLegacy,
        loginSso,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
